/**
 * The footer every A4 document carries on every page: an identifier on the
 * left, "Powered by ForgeDash" in the middle (unless white-label), and
 * "Page n of m" on the right.
 *
 * Two @react-pdf/renderer 4.9 quirks shape this, and both fail silently --
 * the footer is drawn, just outside the page, so the preview on screen shows
 * the branding while every printed and downloaded PDF lost it:
 *
 *   1. A `fixed` view anchored with `bottom` is laid out against the whole
 *      document, not the page, and lands far above page 1. Anchor with `top`
 *      measured from the A4 height instead.
 *   2. Plain `<Text>` children of a `fixed` view stay out of place even then;
 *      only text drawn through `render` is positioned per page. So every piece
 *      of text here uses `render`, including the static ones.
 *
 * Verified by rendering one- and two-page invoices and reading the text
 * positions back with pdf.js (2026-10-19).
 */
import { Text, View, StyleSheet } from '@react-pdf/renderer';
import { DOC } from './documentPalette';

/** A4 portrait height in PDF points. */
const A4_HEIGHT = 841.89;
/** Distance from the bottom edge to the top of the footer row. */
const FOOTER_OFFSET = 30;

const s = StyleSheet.create({
    footer: {
        position: 'absolute',
        top: A4_HEIGHT - FOOTER_OFFSET,
        left: 35,
        right: 35,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: 4,
    },
    text: { fontSize: 7, color: DOC.label },
    brand: { fontSize: 7, color: DOC.black },
});

/** Pages using this need `paddingBottom` of at least 40 so content clears it. */
export function PdfPageFooter({ reference, branded }: { reference: string; branded: boolean }) {
    return (
        <View style={s.footer} fixed>
            <Text style={s.text} render={() => reference} />
            {branded && <Text style={s.brand} render={() => 'Powered by ForgeDash'} />}
            <Text style={s.text} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
        </View>
    );
}
